import { Divider, Icon } from '@evrekadev/evreka-ui-components'
import { UilCheckCircle, UilExclamationCircle, UilTimesCircle } from '@iconscout/react-unicons'
import { Flex, HyperLink, Typography } from 'components'
import { useTranslation } from 'utils/useTranslation'

type ItemType = { id: string | number; name: string }

type FailureListType = Array<ItemType & { type: string }>
type SuccessListType = Array<ItemType>
type WarningListType = Array<
  ItemType & {
    description: string
    type: string
  }
>

export const BulkActionToastContent: React.FC<{
  failure_list: FailureListType
  success_list: SuccessListType
  warning_list?: WarningListType
  toastTranslationKey: string
  message: string
  detailLinkRoute?: string
}> = ({
  failure_list,
  success_list,
  warning_list,
  toastTranslationKey,
  detailLinkRoute,
  message,
}) => {
  const { t } = useTranslation(`pages.bulk_actions.${toastTranslationKey}`)

  const failure_list_object = failure_list.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = []
      }
      acc[item.type].push(item)
      return acc
    },
    {} as Record<string, FailureListType>,
  )

  const warning_list_object = warning_list?.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = []
      }
      acc[item.type].push(item)
      return acc
    },
    {} as Record<string, WarningListType>,
  )

  return (
    <Flex directionColumn="zero" gap={0.25} style={{ overflow: 'auto', maxHeight: '80vh' }}>
      <Flex directionColumn="zero">
        <Typography variant="span" fontSize="text_sm">
          {message}
        </Typography>
        <Flex gap={0.25}>
          <Flex gap={0.25}>
            <Icon icon={UilCheckCircle} color="green500" size={20} />
            <Typography variant="span" fontSize="text_sm" color="green500" fontWeight="medium">
              {success_list.length.toString()}
            </Typography>
          </Flex>

          <Flex gap={0.25}>
            <Icon icon={UilTimesCircle} color="red500" size={20} />
            <Typography variant="span" fontSize="text_sm" color="red500" fontWeight="medium">
              {failure_list.length.toString()}
            </Typography>
          </Flex>
          {warning_list && warning_list.length > 0 && (
            <Flex gap={0.25}>
              <Icon icon={UilExclamationCircle} color="orange500" size={20} />
              <Typography variant="span" fontSize="text_sm" color="orange500" fontWeight="medium">
                {warning_list.length.toString()}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Divider />

      {Object.entries(failure_list_object).map(
        ([key, list], index) =>
          list.length > 0 && (
            <Flex key={key} directionColumn="zero" gap={0.25}>
              <Flex directionColumn="zero">
                <Typography variant="span" color="grey700" fontSize="text_sm" fontWeight="semibold">
                  {`${t(key)} - ${list.length.toString()}`}
                </Typography>

                {list.map(({ id, name }) =>
                  detailLinkRoute ? (
                    <HyperLink
                      target="_blank"
                      value={name}
                      key={id}
                      to={`${detailLinkRoute}${id}`}
                    />
                  ) : (
                    <Typography variant="span">{name}</Typography>
                  ),
                )}
              </Flex>

              {Object.entries(failure_list_object).length !== index + 1 && <Divider />}
            </Flex>
          ),
      )}
      {warning_list_object && (
        <>
          {failure_list.length > 0 && <Divider />}
          {Object.entries(warning_list_object).map(
            ([key, list], index) =>
              list.length > 0 && (
                <Flex key={key} directionColumn="zero" gap={0.25}>
                  <Flex directionColumn="zero">
                    <Typography
                      variant="span"
                      color="grey700"
                      fontSize="text_sm"
                      fontWeight="semibold"
                    >
                      {t(key)}
                    </Typography>

                    {list.map(({ id, name, description }) =>
                      detailLinkRoute ? (
                        <HyperLink
                          target="_blank"
                          value={`${name} - ${description}`}
                          key={id}
                          to={`${detailLinkRoute}${id}`}
                        />
                      ) : (
                        <Typography variant="span">
                          {name} - {description}
                        </Typography>
                      ),
                    )}
                  </Flex>

                  {Object.entries(warning_list_object).length !== index + 1 && <Divider />}
                </Flex>
              ),
          )}
        </>
      )}
    </Flex>
  )
}
